import React, { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { ModalWithLoaderAction, RequestStatus } from '../../Tools/ModalWithLoader/ModalWithLoader';
import { Form, Formik, useField, useFormikContext } from 'formik';
import { Alert, Box, Button, Checkbox, Grid, TextField } from '@mui/material';
import axios from 'axios';
import { Clear, Upload } from '@mui/icons-material';
import { ParamsConfigType } from '../../Admin/Params/Types';
import { BirdzTitle } from '@applications-terrains/birdz-react-library';
import { formFields } from './params.config';

const FileField = ({
  name,
  accept,
  value,
  deleteLabel = 'Supprimer',
  choseFileLabel = 'Choisir un fichier'
}: any) => {
  const { setFieldValue } = useFormikContext();
  const [field] = useField(name);
  const fieldValue = value || field.value;

  const toBase64 = (file: File): Promise<string> => {
    return new Promise((resolve, reject) => {
      const reader = new FileReader();
      reader.readAsDataURL(file);
      reader.onload = () => resolve(reader.result as string);
      reader.onerror = (error) => reject(error);
    });
  };

  return (
    <span>
      {fieldValue ? (
        <>
          <Box>
            <img src={fieldValue} width="200px" alt="Preview" />
          </Box>
          <Button
            onClick={() => setFieldValue(name, '')}
            size="small"
            variant="outlined"
            color="error"
            sx={{ mt: 1 }}
          >
            <Clear sx={{ mr: 1 }} /> {deleteLabel}
          </Button>
        </>
      ) : (
        <Button variant="outlined" component="label">
          <Upload sx={{ mr: 1 }} /> {choseFileLabel}
          <input
            type="file"
            hidden
            accept={accept}
            onChange={async (event) => {
              const file = event.target.files?.[0];
              if (file && file.type.startsWith('image/')) {
                const base64 = await toBase64(file);
                setFieldValue(name, base64);
              }
            }}
          />
        </Button>
      )}
    </span>
  );
};

export default function FieldsForm({
  endpoint,
  setRequestStatus,
  paramConfig,
  action,
  setAction,
  id
}: {
  endpoint: string;
  setRequestStatus: (value: RequestStatus) => void;
  paramConfig: ParamsConfigType;
  action: ModalWithLoaderAction;
  setAction: (value: ModalWithLoaderAction) => void;
  id: string | null;
}) {
  const [values, setValues] = useState<any>();

  const { t } = useTranslation();

  useEffect(() => {
    if (action === 'add' && paramConfig.value === 'families') {
      const fields = {
        id: null,
        name: null,
        icon: null,
        intervention_expiry_days: null
      };
      setValues(fields);
      paramConfig.label = t('equipmentFamilies.addFamily');
    }
    if (action === 'edit' && paramConfig.value === 'families') {
      axios.get(`/api/boi/exocet/families/${id}/`).then((response) => {
        setValues(response.data);
      });
      paramConfig.label = t('equipmentFamilies.editFamily');
    }

    if (action === 'add' && paramConfig.value === 'configuration') {
      const fields = {
        name: null,
        dataset_code: null,
        dst: false,
        time_index: false,
        autoconnect: false,
        is_default: false
      };
      setValues(fields);
    }
    if (action === 'edit' && paramConfig.value === 'configuration') {
      axios.get(`/api/boi/params/configuration-profiles/${id}/`).then((response) => {
        setValues(response.data);
      });
    }
  }, [action]);

  const [error, setError] = useState<string | null>(null);

  const handleSubmit = async (values: any) => {
    //errors families
    if (paramConfig.value === 'families') {
      if (!values.id || !values.name || !values.intervention_expiry_days) {
        setError(t('equipments.fieldsForm.errors.requiredFields'));
        return;
      }
      if (values.id < 0) {
        setError(t('equipments.fieldsForm.errors.idPositive'));
        return;
      }
      if (values.intervention_expiry_days < 0) {
        setError(t('equipments.fieldsForm.errors.expiryDaysPositive'));
        return;
      }
    }

    if (values.name && values.name.includes(' ')) {
      setError(t('equipments.fieldsForm.errors.nameNoSpaces'));
      return;
    }

    //errors config
    if (paramConfig.value === 'configuration') {
      if (!values.name || !values.dataset_code) {
        setError(t('equipments.fieldsForm.errors.nameDatasetRequired'));
        return;
      }
    }

    if (values.dataset_code && values.dataset_code.includes(' ')) {
      setError(t('equipments.fieldsForm.errors.datasetNoSpaces'));
      return;
    }

    const payload = values;
    if (payload.icon === null) {
      delete payload.icon;
    }

    if (endpoint) {
      if (!id) {
        await axios.post(endpoint, payload).then(() => {
          setRequestStatus('success');
          setError(null);
          setTimeout(() => {
            setRequestStatus(null);
            setAction(null);
            // navigate(-1);
          }, 1500);
        });
      } else {
        await axios.put(endpoint + `${id}`, payload).then(() => {
          setRequestStatus('success');
          setError(null);
          setTimeout(() => {
            setRequestStatus(null);
            setAction(null);
            // navigate(-1);
          }, 1500);
        });
      }
    }
  };
  if (!values) return null;

  return (
    <Formik initialValues={values} onSubmit={handleSubmit}>
      {({ handleChange, values, setFieldValue }) => (
        <Form>
          {values && (
            <Grid container>
              <Grid item xs={12} sx={{ textAlign: 'center' }}>
                <BirdzTitle>{t(paramConfig.label)}</BirdzTitle>
              </Grid>
              {values &&
                Object.entries(values).map(([key, value]) => {
                  const element = formFields[key as keyof typeof formFields];
                  return (
                    <>
                      {element && element.type === 'image' ? (
                        <>
                          <Grid item xs={4} sx={{ mt: 2 }}>
                            {t('equipments.fieldsForm.icon')}
                          </Grid>
                          <Grid item xs={8} sx={{ mt: 2 }}>
                            <FileField
                              name="icon"
                              accept="image/*"
                              deleteLabel={t('equipments.fieldsForm.deletePhoto')}
                              choseFileLabel={t('equipments.fieldsForm.choosePhoto')}
                            />
                          </Grid>
                        </>
                      ) : element && element.type === 'checkbox' ? (
                        <>
                          <Grid item xs={4} sx={{ mt: 2 }}>
                            {element && t(element.label)}
                          </Grid>
                          <Grid item xs={8}>
                            <Checkbox
                              checked={values.key}
                              onChange={(e) => {
                                setFieldValue(key, e.target.checked);
                              }}
                              name={key}
                            />
                          </Grid>
                        </>
                      ) : element &&
                        element.label === 'ID' &&
                        id &&
                        paramConfig.value === 'families' ? (
                        <>
                          <Grid item xs={4} sx={{ mt: 2 }}>
                            {element && t(element.label)}
                          </Grid>
                          <Grid item xs={8} sx={{ mt: 1 }}>
                            <TextField
                              size="small"
                              type={element && element.type}
                              name={key}
                              value={value}
                              fullWidth
                              disabled
                            />
                          </Grid>
                        </>
                      ) : element &&
                        (element.name === 'name' ||
                          element.name === 'dataset_code' ||
                          element.name === 'dataset' ||
                          element.name === 'intervention_expiry_days' ||
                          (element.name === 'id' && paramConfig.value === 'families')) ? (
                        <>
                          <Grid item xs={4} sx={{ mt: 2 }}>
                            {element && t(element.label)}
                          </Grid>
                          <Grid item xs={8} sx={{ mt: 1 }}>
                            <TextField
                              size="small"
                              type={element && element.type}
                              name={key}
                              value={value}
                              fullWidth
                              onChange={handleChange}
                            />
                          </Grid>
                        </>
                      ) : null}
                    </>
                  );
                })}

              {error && (
                <Grid item xs={12} sx={{ mt: 2 }}>
                  <Alert severity="error">{error}</Alert>
                </Grid>
              )}

              <Grid container justifyContent="center" sx={{ mt: 2 }}>
                <Grid item>
                  <Button type="submit" variant="contained">
                    {id ? t('equipments.fieldsForm.edit') : t('equipments.fieldsForm.add')}
                  </Button>
                  <Button
                    sx={{ ml: 2 }}
                    onClick={() => {
                      setRequestStatus(null);
                      setAction(null);
                      setError(null);
                      // navigate(-1);
                    }}
                  >
                    {t('equipments.fieldsForm.cancel')}
                  </Button>
                </Grid>
              </Grid>
            </Grid>
          )}
        </Form>
      )}
    </Formik>
  );
}
